import ElakayImage from '../../../components/images/ElakayImage';
import React, { useCallback, useState } from "react";
import ShopHeroSourcePanel from "../../dynamicSwipers/ShopHeroSourcePanel";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../../Layout/Layout";
import { toImageUrl } from "../../../config";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import { SHOP_TEMPLATES } from "../../pageInside/PageBuilder/ShopBoutiqueTemplates";
import DropshippingInterneLayout from "../components/DropshippingInterneLayout";
import { getProviderSettings } from "../services/wholesaleService";
import { getOwnStorefront, publishStorefront, saveStorefrontDraft, unpublishStorefront, updateStorefrontPages } from "../services/storefrontService";
import { PROVIDER_ROUTES, providerStorefrontPath } from "../providerWorkspace";
import { buildProviderShopAdapter, ProviderShopAbout, ProviderShopHeader, ProviderShopHome } from "../storefront/ProviderShop";
import "../storefront/providerShop.css";
import useProviderStorefront from "../storefront/useProviderStorefront";
import { addPage, blankPage, duplicatePage, pageFromTemplate, removePage, renamePage } from "../storefront/storefrontPages";
import { providerAboutTemplatePage } from "../storefront/providerAboutDoc";
import { tokenOf, useLoad } from "./PageShell";
import OrdersPage from "./OrdersPage";
import ProviderNotice from "./ProviderNotice";
import { StorefrontBadge } from "./ProviderCenter";
import { storefrontShareUrl } from "./storefrontData";
import "../seller/DropshippingIn.css";
import "./center.css";
import "./providerManagement.css";
import "./providerCenter.css";
import { providerShopMonogram } from "../utils/providerShopIdentity";
import { formatElakayDateTime } from "../../../utils/elakayDate";
import ShopBar from "../../shops/ShopBar";
import { useActiveShopChange } from "../../shops/accountShopsStore";

const ok = (r) => r && !r.error && r.success !== false;
const when = (d) => (d ? formatElakayDateTime(d) || "—" : "—");
const VIEWS = ["admin", "customer", "orders"];
const PROVIDER_ABOUT_TEMPLATE = "provider-about";

const loadOwner = async (token) => {
  const [own, settings] = await Promise.all([getOwnStorefront(token), getProviderSettings(token)]);
  if (!ok(own)) return own;
  return { ...own, providerSettings: ok(settings) ? settings.settings : null };
};

const Toggle = ({ on }) => on == null
  ? <span className="pvc-badge pvc-badge--none">—</span>
  : <span className={`pvc-badge pvc-badge--${on ? "on" : "off"}`}>{on ? text("h_pvc_on", "Enabled") : text("h_pvc_off", "Disabled")}</span>;

const PageStatus = ({ row }) => {
  if (!row.inDraft) return <span className="pvc-badge pvc-badge--changes">{text("h_pvc_page_live_only", "Live — deleted from draft")}</span>;
  if (!row.published) return <span className="pvc-badge pvc-badge--draft">{text("h_pvc_page_draft", "Draft only")}</span>;
  if (row.hasUnpublishedChanges) return <span className="pvc-badge pvc-badge--changes">{text("h_pvc_page_changes", "Published · changes to publish")}</span>;
  return <span className="pvc-badge pvc-badge--published">{text("h_pvc_status_published", "Published")}</span>;
};

// ── ADMIN VIEW: pages (Home, navigation, publication), storefront settings, business summary ──
function AdminView({ data, live, busy, run, openCustomer, infoSource }) {
  const { storefront, page, profile } = data;
  const rows = storefront?.pages || [];
  const [newName, setNewName] = useState("");
  const [template, setTemplate] = useState("");
  const [renaming, setRenaming] = useState(null);
  const shareUrl = storefrontShareUrl(profile);
  const providerShopUrl = profile?.provider?._id
    ? providerStorefrontPath(
      profile.provider._id,
      null,
      profile?.currentShop && !profile.currentShop.isPrimary ? profile.currentShop._id : null
    )
    : null;
  const settings = storefront?.settings || { showHeader: true, catalog: { show: true, position: "after" } };
  const stats = live?.storefront?.stats || {};
  const title = page?.title || profile?.shop?.shopName || text("h_pvc_default_title", "Provider storefront");
  const saveDoc = (doc, message) => run(() => saveStorefrontDraft(tokenOf(), { doc, title, description: page?.description || "" }), message);
  const patch = (body, message) => run(() => updateStorefrontPages(tokenOf(), body), message);

  const createPage = (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    const tpl = SHOP_TEMPLATES.find((t) => t._id === template);
    const { page: created, meta } = template === PROVIDER_ABOUT_TEMPLATE
      ? { page: providerAboutTemplatePage({ name: profile?.shop?.shopName, roleName: profile?.provider?.roleName }, name), meta: null }
      : tpl ? pageFromTemplate(tpl, name) : { page: blankPage(name), meta: null };
    saveDoc(addPage(page?.doc || null, created, meta), text("h_pvc_page_created", "Page “{value0}” created as a draft.", { value0: name }));
    setNewName(""); setTemplate("");
  };
  const move = (index, delta) => {
    const next = [...rows];
    const [row] = next.splice(index, 1);
    next.splice(index + delta, 0, row);
    patch({ pages: next.map((r, i) => ({ pageId: r.id, navigationOrder: i })) }, text("h_pvc_order_saved", "Navigation order saved."));
  };

  return (
    <div className="pvc-layout">
      <div>
        {/* Main Shop Slider Source (same panel as the Seller's My Shop Pub). */}
        <ShopHeroSourcePanel infoSource={infoSource} shopUrl={providerShopUrl} />
        <section className="pvc-panel" aria-labelledby="pvc-pages-title">
          <h2 id="pvc-pages-title"><i className="fas fa-layer-group" aria-hidden="true" />{text("h_pvc_pages_title", "Pages & navigation")}</h2>
          <p>{text("h_pvc_pages_sub", "Every page of your storefront. Choose the Home page, which pages appear in the storefront menu, and publish pages when they are ready.")}</p>
          {rows.length > 0 ? (
            <div className="ctr__tablewrap">
              <table className="pvc-pages">
                <thead><tr>
                  <th scope="col">{text("h_pvc_col_page", "Page")}</th>
                  <th scope="col">{text("h_pvc_col_home", "Home")}</th>
                  <th scope="col">{text("h_pvc_col_about", "About us")}</th>
                  <th scope="col">{text("h_pvc_col_nav", "In menu")}</th>
                  <th scope="col">{text("h_pvc_col_order", "Order")}</th>
                  <th scope="col">{text("h_pvc_status", "Status")}</th>
                  <th scope="col">{text("h_pvc_col_actions", "Actions")}</th>
                </tr></thead>
                <tbody>{rows.map((row, i) => (
                  <tr key={row.id} data-page-row={row.id}>
                    <td>{renaming?.id === row.id
                      ? <form className="pvc-inline" onSubmit={(e) => { e.preventDefault(); const name = renaming.name.trim(); if (name) saveDoc(renamePage(page.doc, row.id, name), text("h_pvc_renamed", "Page renamed.")); setRenaming(null); }}>
                          <input aria-label={text("h_pvc_rename", "Rename")} value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} autoFocus />
                          <button type="submit" className="ctr__btn">{text("h_pvc_save", "Save")}</button>
                        </form>
                      : <strong>{row.name}</strong>}</td>
                    <td><input type="radio" name="pvc-home" aria-label={text("h_pvc_set_home", "Set {value0} as Home", { value0: row.name })} checked={row.isHome} disabled={busy} onChange={() => patch({ homePageId: row.id }, text("h_pvc_home_saved", "“{value0}” is now your Home page.", { value0: row.name }))} /></td>
                    <td><input type="radio" name="pvc-about" aria-label={text("h_pvc_set_about", "Use {value0} as the About us page", { value0: row.name })} checked={Boolean(row.isAbout)} disabled={busy} onChange={() => patch({ aboutPageId: row.id }, text("h_pvc_about_saved", "“{value0}” is now your About us page.", { value0: row.name }))} /></td>
                    <td><input type="checkbox" aria-label={text("h_pvc_show_in_nav", "Show {value0} in the storefront menu", { value0: row.name })} checked={row.showInNavigation} disabled={busy} onChange={(e) => patch({ pages: [{ pageId: row.id, showInNavigation: e.target.checked }] }, text("h_pvc_nav_saved", "Navigation saved."))} /></td>
                    <td className="pvc-pages__order">
                      <button type="button" className="ctr__btn" disabled={busy || i === 0} aria-label={text("h_pvc_move_up", "Move {value0} up", { value0: row.name })} onClick={() => move(i, -1)}>↑</button>
                      <button type="button" className="ctr__btn" disabled={busy || i === rows.length - 1} aria-label={text("h_pvc_move_down", "Move {value0} down", { value0: row.name })} onClick={() => move(i, 1)}>↓</button>
                    </td>
                    <td><PageStatus row={row} />{row.publishedAt && <small className="pvc-pages__date">{when(row.publishedAt)}</small>}</td>
                    <td className="pvc-pages__actions">
                      {row.published && <button type="button" className="ctr__btn" onClick={() => openCustomer(row.id)}>{text("h_pvc_view", "View")}</button>}
                      {row.inDraft && <Link className="ctr__btn" to={PROVIDER_ROUTES.builderPage(row.id)}>{text("h_pvc_edit", "Edit")}</Link>}
                      {row.inDraft && (!row.published || row.hasUnpublishedChanges) && <button type="button" className="ctr__btn ctr__btn--primary" disabled={busy} onClick={() => run(() => publishStorefront(tokenOf(), [row.id]), text("h_pvc_page_published", "“{value0}” is published.", { value0: row.name }))}>{text("h_pvc_publish", "Publish")}</button>}
                      {row.published && <button type="button" className="ctr__btn" disabled={busy} onClick={() => run(() => unpublishStorefront(tokenOf(), [row.id]), text("h_pvc_page_unpublished", "“{value0}” is no longer visible to Sellers.", { value0: row.name }))}>{text("h_pvc_unpublish", "Unpublish")}</button>}
                      {row.inDraft && <button type="button" className="ctr__btn" disabled={busy} onClick={() => setRenaming({ id: row.id, name: row.name })}>{text("h_pvc_rename", "Rename")}</button>}
                      {row.inDraft && <button type="button" className="ctr__btn" disabled={busy} onClick={() => saveDoc(duplicatePage(page.doc, row.id, text("h_pvc_copy_of", "Copy of {value0}", { value0: row.name })), text("h_pvc_duplicated", "Page duplicated."))}>{text("h_pvc_duplicate", "Duplicate")}</button>}
                      {row.inDraft && rows.filter((r) => r.inDraft).length > 1 && <button type="button" className="ctr__btn ctr__btn--danger" disabled={busy}
                        onClick={() => { if (window.confirm(text("h_pvc_delete_confirm", "Delete “{value0}” from your draft? If it is published it stays visible until you publish all or unpublish it.", { value0: row.name }))) saveDoc(removePage(page.doc, row.id), text("h_pvc_deleted", "Page deleted from the draft.")); }}>{text("h_pvc_delete", "Delete")}</button>}
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : null}
          {rows.length > 0 && (
            <p className="pvc-builder__hint">
              {storefront?.aboutPageId
                ? <>{text("h_pvc_about_custom", "Your shop's “About us” link opens the page you chose (once it is published).")}{" "}
                    <button type="button" className="ctr__btn" disabled={busy} onClick={() => patch({ aboutPageId: null }, text("h_pvc_about_reset", "Your shop uses the generated About us page again."))}>{text("h_pvc_about_use_generated", "Use the generated About page")}</button></>
                : text("h_pvc_about_generated", "No About page chosen: your shop shows a generated About us page built from your real Provider information.")}
            </p>
          )}
          {rows.length > 0 ? null : <div className="ctr__empty"><i className="fas fa-paint-brush" aria-hidden="true" /><p>{text("h_pvc_no_draft", "You have not designed a custom storefront yet. Sellers see your default Provider storefront.")}</p></div>}

          <form className="pvc-newpage" onSubmit={createPage}>
            <label>{text("h_pvc_new_page", "New page name")}<input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={text("h_pvc_new_page_ph", "e.g. Wholesale, About Us, Contact")} /></label>
            <label>{text("h_pvc_start_from", "Start from")}
              <select value={template} onChange={(e) => setTemplate(e.target.value)}>
                <option value="">{text("h_pvc_blank", "Blank page")}</option>
                <option value={PROVIDER_ABOUT_TEMPLATE}>{text("h_pvc_tpl_about", "Provider About Us")}</option>
                {SHOP_TEMPLATES.map((t) => <option key={t._id} value={t._id}>{t.label}</option>)}
              </select>
            </label>
            <button type="submit" className="ctr__btn ctr__btn--primary" disabled={busy || !newName.trim()}><i className="fas fa-plus" aria-hidden="true" />{text("h_pvc_create_page", "Create page")}</button>
          </form>
          <p className="pvc-builder__hint">{text("h_pvc_template_hint", "A template is only a starting layout: your page is your own copy, and editing it never changes the template.")}</p>
        </section>

        <section className="pvc-panel" aria-labelledby="pvc-sfsettings-title">
          <h2 id="pvc-sfsettings-title"><i className="fas fa-sliders-h" aria-hidden="true" />{text("h_pvc_sf_settings", "Storefront settings")}</h2>
          <div className="pmgmt-setting-row">
            <div><label htmlFor="pvc-show-header">{text("h_pvc_show_header", "Storefront header & menu")}</label><p>{text("h_pvc_show_header_help", "Your logo, business name and page menu above your pages. Turn it off if your pages have their own header.")}</p></div>
            <input className="pmgmt-switch" id="pvc-show-header" type="checkbox" role="switch" checked={settings.showHeader} disabled={busy} onChange={(e) => patch({ settings: { showHeader: e.target.checked } }, text("h_pvc_settings_saved", "Storefront settings saved."))} />
          </div>
          <div className="pmgmt-setting-row">
            <div><label htmlFor="pvc-show-catalog">{text("h_pvc_show_catalog", "Automatic product catalogue")}</label><p>{text("h_pvc_show_catalog_help", "Your active products with Buy Wholesale / Add to My Shop, shown on every page that does not already place a Products element.")}</p></div>
            <input className="pmgmt-switch" id="pvc-show-catalog" type="checkbox" role="switch" checked={settings.catalog.show} disabled={busy} onChange={(e) => patch({ settings: { catalog: { show: e.target.checked } } }, text("h_pvc_settings_saved", "Storefront settings saved."))} />
          </div>
          <fieldset className="pvc-radios" disabled={busy || !settings.catalog.show}>
            <legend>{text("h_pvc_catalog_position", "Catalogue position")}</legend>
            {[["after", text("h_pvc_pos_after", "After custom content")], ["before", text("h_pvc_pos_before", "Before custom content")], ["builder", text("h_pvc_pos_builder", "Only where I place a Products element in the Builder")]].map(([value, label]) => (
              <label key={value}><input type="radio" name="pvc-catalog-pos" value={value} checked={settings.catalog.position === value} onChange={() => patch({ settings: { catalog: { position: value } } }, text("h_pvc_settings_saved", "Storefront settings saved."))} />{label}</label>
            ))}
          </fieldset>
        </section>
      </div>

      <aside>
        <section className="pvc-panel" aria-labelledby="pvc-publish-title">
          <h2 id="pvc-publish-title"><i className="fas fa-globe" aria-hidden="true" />{text("h_pvc_publish_title", "Publication")}</h2>
          <dl className="pvc-dl">
            <dt>{text("h_pvc_status", "Status")}</dt><dd><StorefrontBadge storefront={storefront} /></dd>
            <dt>{text("h_pvc_last_saved", "Last saved")}</dt><dd>{when(storefront?.draftUpdatedAt)}</dd>
            <dt>{text("h_pvc_published_at", "Published")}</dt><dd>{when(storefront?.isPublished ? storefront.publishedAt : null)}</dd>
            <dt>{text("h_pvc_visibility", "Visible to")}</dt><dd>{text("h_pvc_visibility_value", "Sellers, Providers and Elakay business partners")}</dd>
          </dl>
          <div className="pvc-actions">
            {storefront?.hasDraft && <button type="button" className="ctr__btn ctr__btn--primary" disabled={busy || (storefront.isPublished && !storefront.hasUnpublishedChanges)} onClick={() => run(() => publishStorefront(tokenOf()), text("h_pvc_published_msg", "Your storefront is published. Sellers now see it on your Provider shop."))}>
              <i className="fas fa-upload" aria-hidden="true" />{storefront.isPublished ? text("h_pvc_publish_all", "Publish all changes") : text("h_pvc_publish_all_pages", "Publish all pages")}
            </button>}
            {storefront?.isPublished && <button type="button" className="ctr__btn ctr__btn--danger" disabled={busy} onClick={() => run(() => unpublishStorefront(tokenOf()), text("h_pvc_unpublished_msg", "Your storefront is unpublished. Sellers see your default Provider storefront again."))}>
              <i className="fas fa-eye-slash" aria-hidden="true" />{text("h_pvc_unpublish_all", "Unpublish storefront")}
            </button>}
            <Link to={PROVIDER_ROUTES.builder} className="ctr__btn"><i className="fas fa-paint-brush" aria-hidden="true" />{text("h_pvc_open_builder", "Open Page Builder")}</Link>
          </div>
        </section>

        <section className="pvc-panel" aria-labelledby="pvc-share-title">
          <h2 id="pvc-share-title"><i className="fas fa-share-alt" aria-hidden="true" />{text("h_pvc_share_title", "Share storefront")}</h2>
          <p>{text("h_pvc_share_sub", "Only signed-in Sellers and professional roles can open this link.")}</p>
          <ShareBox url={shareUrl} />
          {profile?.provider?._id && <div className="pvc-links"><Link to={providerStorefrontPath(profile.provider._id)}>{text("h_pvc_open_as_seller", "Open as Sellers see it")}</Link></div>}
        </section>

        <section className="pvc-panel" aria-labelledby="pvc-info-title">
          <h2 id="pvc-info-title"><i className="fas fa-id-card" aria-hidden="true" />{text("h_pvc_store_info", "Store information")}</h2>
          <dl className="pvc-dl">
            <dt>{text("h_pvc_business_name", "Business name")}</dt><dd>{profile?.shop?.shopName || "—"}</dd>
            <dt>{text("h_pvc_location", "Location")}</dt><dd>{profile?.shop?.location || "—"}</dd>
            <dt>{text("h_pvc_stat_products", "Active products")}</dt><dd>{stats.activeProducts ?? "—"}</dd>
          </dl>
          <div className="pvc-links"><Link to="/profile?addr=1"><i className="fas fa-pen" aria-hidden="true" />{text("h_pvc_edit_business", "Edit business address")}</Link><Link to={PROVIDER_ROUTES.products}>{text("h_pvc_nav_products", "My Products")}</Link></div>
        </section>

        <section className="pvc-panel" aria-labelledby="pvc-services-title">
          <h2 id="pvc-services-title"><i className="fas fa-handshake" aria-hidden="true" />{text("h_pvc_services", "Wholesale & dropshipping")}</h2>
          <dl className="pvc-dl">
            <dt>{text("h_pvc_wholesale_orders", "Wholesale orders")}</dt><dd><Toggle on={data.providerSettings ? data.providerSettings.acceptWholesaleOrders : null} /></dd>
            <dt>{text("h_pvc_wholesale_products", "Wholesale products")}</dt><dd>{stats.wholesaleProducts ?? "—"}</dd>
            <dt>{text("h_pvc_dropshipping", "Dropshipping")}</dt><dd><Toggle on={data.providerSettings ? data.providerSettings.acceptDropshipping : null} /></dd>
            <dt>{text("h_pvc_dropshipping_products", "Dropshipping products")}</dt><dd>{stats.dropshippingProducts ?? "—"}</dd>
          </dl>
          <div className="pvc-links">
            <Link to={PROVIDER_ROUTES.wholesale}>{text("h_pvc_nav_wholesale", "Wholesale Management")}</Link>
            <Link to={PROVIDER_ROUTES.dropshipping}>{text("h_pvc_nav_dropshipping", "Dropshipping Management")}</Link>
            <Link to={PROVIDER_ROUTES.settings}>{text("h_pvc_nav_settings", "Provider Settings")}</Link>
          </div>
        </section>
      </aside>
    </div>
  );
}

const ShareBox = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { setCopied(false); } };
  return (
    <div className="pvc-share">
      <input readOnly value={url} aria-label={text("h_pvc_share_title", "Share storefront")} onFocus={(e) => e.target.select()} />
      <button type="button" className="ctr__btn" onClick={copy} disabled={!url}>{copied ? text("h_pvc_copied", "Link copied") : text("h_pvc_copy", "Copy")}</button>
    </div>
  );
};

const HelpPanel = () => (
  <section className="pvc-help" aria-labelledby="pvc-help-title">
    <h2 id="pvc-help-title">{text("h_pvc_help_title", "How your Provider storefront works")}</h2>
    <ol>
      <li>{text("h_pvc_help_1", "Admin View › Pages & navigation: create pages (blank or from a template), choose your Home page and which pages appear in the menu.")}</li>
      <li>{text("h_pvc_help_2", "Builder: design a page with the Elakay Page Builder. The 🏭 Provider group adds your real products, wholesale and dropshipping sections, categories and statistics. Save keeps a draft.")}</li>
      <li>{text("h_pvc_help_3", "Preview draft (here) shows exactly what Sellers will see, before publishing.")}</li>
      <li>{text("h_pvc_help_4", "Publish a page (or all pages): Sellers see the published version only; later edits stay private until you publish again.")}</li>
    </ol>
  </section>
);

// ── CUSTOMER VIEW: the real storefront website, with a thin owner toolbar OUTSIDE it ──
// The shop website links on the owner route: /provider/my-shop?view=customer[&section=about][&page=].
const CUSTOMER_PATHS = {
  home: `${PROVIDER_ROUTES.myShop}?view=customer`,
  about: `${PROVIDER_ROUTES.myShop}?view=customer&section=about`,
  page: (id) => `${PROVIDER_ROUTES.myShop}?view=customer&page=${encodeURIComponent(id)}`,
};

function CustomerView({ data, live, infoSource, pageId, section = "home", setPageId }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [draft, setDraft] = useState(false);
  const [help, setHelp] = useState(false);
  const { storefront, page } = data;
  const draftCustom = page?.doc ? {
    doc: page.doc,
    htmlSource: page.htmlSource,
    htmlSourcePages: page.htmlSourcePages,
    homePageId: storefront?.homePageId,
    aboutPageId: storefront?.aboutPageId || null,
    pages: (storefront?.pages || []).filter((r) => r.inDraft),
  } : null;
  const liveCustom = live.storefront?.custom || null;
  const custom = draft ? draftCustom : liveCustom;
  const options = custom?.pages || [];
  const current = pageId || custom?.homePageId || options[0]?.id || "";
  return (
    <>
      <div className="pvc-ownerbar" role="toolbar" aria-label={text("h_pvc_owner_toolbar", "Storefront owner tools")}>
        <button type="button" aria-pressed={!draft && !help} onClick={() => { setDraft(false); setHelp(false); }}>🏪 {text("h_pvc_tool_shop", "Shop")}</button>
        <label className="pvc-ownerbar__select">🎨 {text("h_pvc_tool_pages", "Page & Content")}
          <select value={current} onChange={(e) => setPageId(e.target.value)} disabled={!options.length}>
            {!options.length && <option value="">{text("h_pvc_status_default", "Default storefront")}</option>}
            {options.map((r) => <option key={r.id} value={r.id}>{r.name}{r.isHome ? ` · ${text("h_pvc_col_home", "Home")}` : ""}{draft && !r.published ? ` · ${text("h_pvc_page_draft", "Draft only")}` : ""}</option>)}
          </select>
        </label>
        <Link to={`${PROVIDER_ROUTES.settings}#pvs-dropshipping`} title={text("h_pvc_target_help", "Your storefront follows your Provider regional rules (allowed / blocked countries).")}>📍 {text("h_pvc_tool_target", "Target Location")}</Link>
        <Link to={current && storefront?.pages?.some((r) => r.id === current && r.inDraft) ? PROVIDER_ROUTES.builderPage(current) : PROVIDER_ROUTES.builder}>✏️ {text("h_pvc_tool_builder", "Builder")}</Link>
        <button type="button" aria-pressed={draft} disabled={!draftCustom} onClick={() => { setDraft((d) => !d); setHelp(false); }}>👁 {text("h_pvc_tool_preview", "Preview draft")}</button>
        <button type="button" aria-pressed={help} onClick={() => setHelp((h) => !h)}>❓ {text("h_pvc_tool_help", "Help")}</button>
        <span className={`pvc-badge ${draft ? "pvc-badge--draft" : liveCustom ? "pvc-badge--published" : "pvc-badge--none"}`} role="status">
          {draft ? text("h_pvc_viewing_draft", "Draft preview — not visible to Sellers") : liveCustom ? text("h_pvc_viewing_live", "Published — what Sellers see") : text("h_pvc_status_default", "Default storefront")}
        </span>
      </div>
      {help ? <div className="dsi-page ctr"><HelpPanel /></div> : (
        <div className="pvc-canvas">
          {live.loading && !live.storefront
            ? <div className="dsi-skeleton" role="status" aria-busy="true"><div className="dsi-skeleton__shop" /></div>
            : live.error
              ? <div className="dsi-state-card" role="alert"><p>{live.error}</p></div>
              : live.storefront && (() => {
                // The exact website Sellers see (shop header, identity, hero/pages, categories, products),
                // with the draft pages when "Preview draft" is on.
                const summary = draft ? { ...live.storefront, custom: draftCustom } : live.storefront;
                const common = { summary, previewProducts: live.products, categories: live.categories, paths: CUSTOMER_PATHS, mode: "owner", infoSource, token: tokenOf() };
                return (
                  <div className="psh-page" data-shop-type="provider">
                    <ProviderShopHeader infoSource={infoSource} shop={buildProviderShopAdapter(summary)} paths={CUSTOMER_PATHS} section={section} activePageId={current || null}
                      onRefresh={() => { live.reload(); setRefreshKey((k) => k + 1); }} inline />
                    {section === "about"
                      ? <ProviderShopAbout {...common} custom={summary.custom} />
                      : <ProviderShopHome {...common} pageId={current || null} refreshKey={refreshKey} productsLoading={live.loading} />}
                  </div>
                );
              })()}
        </div>
      )}
    </>
  );
}

// /provider/my-shop -- the Provider version of Seller My Shop (same Admin View / Customer View /
// Orders structure; Seller My Shop is never read or written). `?view=` and `?page=` are in the URL,
// so every view and page can be linked and survives a refresh.
export default function ProviderMyShop({ infoSource }) {
  const [params, setParams] = useSearchParams();
  const view = VIEWS.includes(params.get("view")) ? params.get("view") : "admin";
  const pageId = params.get("page");
  const state = useLoad(loadOwner, []);
  const data = state.data || {};
  const live = useProviderStorefront(data.profile?.provider?._id || null);
  const reloadOwner = state.reload;
  const reloadLive = live.reload;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const reloadSelectedShop = useCallback(() => {
    setMsg(null);
    reloadOwner();
    reloadLive();
  }, [reloadOwner, reloadLive]);
  useActiveShopChange("provider", reloadSelectedShop);
  const setQuery = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };
  // Every mutation reloads both the owner data and what Sellers see.
  const run = async (fn, success) => {
    setBusy(true); setMsg(null);
    const r = await fn();
    setBusy(false);
    if (!ok(r)) { setMsg({ ok: false, text: r?.error || text("h_7f2f6a15cf8da2", "Error") }); return; }
    setMsg({ ok: true, text: success });
    state.reload(); live.reload();
  };
  const tabs = [
    ["admin", text("h_pvc_tab_admin", "Admin View")],
    ["customer", text("h_pvc_tab_customer", "Customer View")],
    ["orders", text("h_pvc_tab_orders", "Orders")],
  ];

  return (
    <Layout infoSource={infoSource} titleElakay="providerProducts" SliderPunShow={false} loading={false} outOffPage={false} {...infoSource?.allSettings?.layout} showFooter={true}>
      <DropshippingInterneLayout infoSource={infoSource}>
        <div className="dsi-page ctr pvc-myshop">
          <nav className="dsi-breadcrumb" aria-label={text("h_c766e665183df1", "Breadcrumb")}>
            <Link to={PROVIDER_ROUTES.dashboard}>{text("h_pvc_title", "Provider Center")}</Link><span aria-hidden="true">›</span>
            <span aria-current="page">{text("h_pvc_nav_myshop", "My Provider Shop")}</span>
          </nav>
          <ProviderNotice />
          <ShopBar type="provider" />
          <div className="pvc-viewtabs" role="tablist" aria-label={text("h_pvc_nav_myshop", "My Provider Shop")}>
            {tabs.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={view === key} onClick={() => setQuery({ view: key === "admin" ? "" : key, page: "", section: "" })}>{label}</button>
            ))}
          </div>
          {msg && <div className={`ctr__msg${msg.ok ? "" : " ctr__msg--err"}`} role="status">{msg.text}</div>}
          {state.loading && !state.data && <div className="dsi-skeleton" role="status" aria-busy="true"><div className="dsi-skeleton__shop" /></div>}
          {!state.loading && state.error && <div className="dsi-state-card" role="alert"><h2>{text("h_spm_error_title", "Something went wrong")}</h2><p>{state.error}</p><button type="button" className="dsi-state-card__btn" onClick={state.reload}>{text("h_spm_retry", "Retry")}</button></div>}
          {state.data && view === "admin" && (
            <>
              <header className="pvc-shophead">
                {data.profile?.shop?.avatar
                  ? <ElakayImage preset="avatar" className="pvc-shophead__logo" src={toImageUrl(data.profile.shop.avatar)} alt="" />
                  : <span className="pvc-shophead__logo" aria-hidden="true">{providerShopMonogram(data.profile?.shop, data.profile?.provider)}</span>}
                <div className="pvc-shophead__id">
                  <h1>{data.profile?.shop?.shopName || text("h_pvc_nav_myshop", "My Provider Shop")}</h1>
                  <div className="pvc-shophead__meta">
                    <StorefrontBadge storefront={data.storefront} />
                    {data.storefront?.hasUnpublishedChanges && <span className="pvc-badge pvc-badge--changes">{text("h_pvc_unpublished_changes", "Unpublished changes")}</span>}
                    {data.profile?.shop?.location && <span>{data.profile.shop.location}</span>}
                  </div>
                </div>
                <div className="pvc-shophead__actions">
                  <button type="button" className="ctr__btn" onClick={() => setQuery({ view: "customer", page: "" })}><i className="fas fa-eye" aria-hidden="true" />{text("h_pvc_tab_customer", "Customer View")}</button>
                  <Link to={PROVIDER_ROUTES.builder} className="ctr__btn ctr__btn--primary"><i className="fas fa-paint-brush" aria-hidden="true" />{text("h_pvc_edit_builder", "Edit with Page Builder")}</Link>
                </div>
              </header>
              <AdminView data={data} live={live} busy={busy} run={run} openCustomer={(id) => setQuery({ view: "customer", page: id })} infoSource={infoSource} />
            </>
          )}
          {view === "orders" && <OrdersPage infoSource={infoSource} side="provider" embedded />}
        </div>
        {state.data && view === "customer" && (
          <CustomerView data={data} live={live} infoSource={infoSource} pageId={pageId} section={params.get("section") === "about" ? "about" : "home"} setPageId={(id) => setQuery({ view: "customer", page: id, section: "" })} />
        )}
      </DropshippingInterneLayout>
    </Layout>
  );
}
